import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Chip,
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateOrderMutation } from '../../../services/orderApi';
import { useLazyValidateCouponQuery } from '../../../services/posCouponApi';
import { setCurrentOrder } from '../../../store/orderSlice';
import { useToast } from '../../common/ToastProvider';

// Maps every backend error message to a cashier-friendly string
const friendlyError = (msg = '') => {
  if (msg.includes('not found'))               return 'Invalid coupon code';
  if (msg.includes('not active'))              return 'This coupon is no longer active';
  if (msg.includes('expired'))                 return 'This coupon has expired';
  if (msg.includes('not valid at this store')) return 'Coupon not available at this store';
  if (msg.includes('Customer required'))       return 'Add a customer to use this coupon';
  if (msg.includes('not valid for this customer')) return 'This coupon is not valid for this customer';
  if (msg.includes('Minimum order value'))     return msg; // already has ₹X in it
  if (msg.includes('bill discount'))           return '__BILL_CONFLICT__';
  if (msg.includes('usage limit has been reached')) return 'This coupon is no longer available';
  if (msg.includes('reached the usage limit')) return "You've already used this coupon the maximum number of times";
  return msg || 'Invalid coupon code';
};

const fmt = (v) =>
  `₹${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CouponSection({ onApplied } = {}) {
  const dispatch   = useDispatch();
  const showToast  = useToast();

  const { currentOrderId, coupon_code, coupon_discount_amount, coupons, bill_discount_id } =
    useSelector((s) => s.order);

  const [code, setCode]         = useState('');
  const [error, setError]       = useState('');    // '' | '__BILL_CONFLICT__' | string
  const [isApplying, setIsApplying] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const [validateCoupon] = useLazyValidateCouponQuery();
  const [updateOrder]    = useUpdateOrderMutation();

  const handleCodeChange = (e) => {
    setCode(e.target.value.toUpperCase());
    setError('');
  };

  const handleApply = useCallback(async () => {
    if (!code.trim() || !currentOrderId) return;
    setError('');
    setIsApplying(true);

    try {
      // Step 1: validate (read-only, no side effects)
      const validateRes = await validateCoupon({ code: code.trim(), order_id: currentOrderId }).unwrap();

      if (!validateRes?.data?.valid) {
        setError(friendlyError(validateRes?.data?.error || ''));
        return;
      }

      // Step 2: apply
      const applyRes = await updateOrder({
        id: currentOrderId,
        coupon_code: code.trim(),
      }).unwrap();

      dispatch(setCurrentOrder(applyRes.data.order));
      setCode('');
      showToast('Coupon applied!', 'success');
      onApplied?.();
    } catch (err) {
      // Ceiling/benefit-count guard rejection (benefit-stacking-policy): the
      // coupon was refused but details.order is the corrected order (WITHOUT
      // it) — must resync so any earlier-surviving coupon still renders right
      // (refusal is per requested code, not per request — see doc).
      const guardCode = err?.data?.details?.code;
      if (guardCode === 'discount_ceiling_exceeded' || guardCode === 'max_stacked_benefits_exceeded') {
        if (err.data.details.order) dispatch(setCurrentOrder(err.data.details.order));
        setError(err.data.error || 'Coupon not applied');
        return;
      }
      const msg = err?.data?.error || '';
      const mapped = friendlyError(msg);
      setError(mapped);
    } finally {
      setIsApplying(false);
    }
  }, [code, currentOrderId, validateCoupon, updateOrder, dispatch, showToast, onApplied]);

  const handleRemove = useCallback(async () => {
    if (!currentOrderId) return;
    setIsRemoving(true);
    try {
      const res = await updateOrder({
        id: currentOrderId,
        coupon_code: '',
      }).unwrap();
      dispatch(setCurrentOrder(res.data.order));
      showToast('Coupon removed', 'info');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to remove coupon', 'error');
    } finally {
      setIsRemoving(false);
    }
  }, [currentOrderId, updateOrder, dispatch, showToast]);

  const handleRemoveBillDiscount = useCallback(async () => {
    if (!currentOrderId) return;
    try {
      const res = await updateOrder({ id: currentOrderId, bill_discount: null }).unwrap();
      dispatch(setCurrentOrder(res.data.order));
      setError('');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to remove bill discount', 'error');
    }
  }, [currentOrderId, updateOrder, dispatch, showToast]);

  const isBillConflict = error === '__BILL_CONFLICT__';

  // Uniform list to render regardless of how many are applied: coupons[] once
  // 2+ codes exist, else a single synthetic entry from the legacy aggregate
  // fields (which stay correct for the first/only code — see the doc).
  const appliedCoupons = (coupons && coupons.length > 0)
    ? coupons
    : coupon_code ? [{ id: 'legacy', code: coupon_code, discount_amount: coupon_discount_amount }] : [];
  const hasApplied = appliedCoupons.length > 0;

  // ── Applied chips ────────────────────────────────────────────────────────
  // Backend removal is all-or-nothing (CouponApplicationService#remove calls
  // order_coupons.destroy_all) — there is no endpoint to detach a single code
  // out of several, so every chip shares one "Clear coupons" action rather
  // than a per-chip remove (removing the single applied one IS removing all
  // of them, so the same action is correct whether there's 1 or several).
  //
  // The input row stays visible even with a coupon already applied — so a
  // second/third code can be tried on orgs whose max_coupons_per_order
  // allows it; the reactive "Coupon limit reached" error covers orgs where
  // it can't (see doc: no proactive cap gating).

  return (
    <Box>
      {/* Input row */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          placeholder={hasApplied ? 'Enter another coupon code' : 'Enter coupon code'}
          value={code}
          onChange={handleCodeChange}
          onKeyDown={(e) => e.key === 'Enter' && code.trim().length >= 1 && handleApply()}
          disabled={isApplying}
          error={Boolean(error)}
          inputProps={{
            style: { fontFamily: 'monospace', letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.82rem' },
          }}
          InputProps={{
            startAdornment: (
              <LocalOfferOutlinedIcon sx={{ fontSize: 15, color: error ? '#DC2626' : '#94A3B8', mr: 0.75, flexShrink: 0 }} />
            ),
            sx: { borderRadius: '8px', height: { xs: 32, sm: 36 }, fontSize: '0.82rem', pr: 0.5 },
          }}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          disableElevation
          onClick={handleApply}
          disabled={code.trim().length < 1 || isApplying || !currentOrderId}
          sx={{
            height: { xs: 32, sm: 36 },
            minWidth: 72,
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: '0.78rem',
            textTransform: 'none',
            bgcolor: '#4F46E5',
            flexShrink: 0,
            '&:hover': { bgcolor: '#4338CA' },
            '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
          }}
        >
          {isApplying
            ? <CircularProgress size={14} sx={{ color: '#fff' }} />
            : 'Apply'}
        </Button>
      </Box>

      {/* Helper / error */}
      {!error && (
        <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', mt: 0.6, pl: 0.25 }}>
          Case insensitive · e.g. DIWALI20
        </Typography>
      )}

      {/* Applied coupon chips */}
      {hasApplied && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1 }}>
          {appliedCoupons.map((c) => (
            <Box key={c.id} sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              height: 28, pl: 1, pr: 1.25,
              borderRadius: '999px',
              bgcolor: '#F0FDF4',
              border: '1px solid #BBF7D0',
            }}>
              <CheckCircleIcon sx={{ fontSize: 13, color: '#16A34A', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#15803D', fontFamily: 'monospace', letterSpacing: '0.03em' }}>
                {c.code}
              </Typography>
              {parseFloat(c.discount_amount || 0) > 0 && (
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#16A34A' }}>
                  · −{fmt(c.discount_amount)}
                </Typography>
              )}
            </Box>
          ))}
          <Button
            size="small"
            disabled={isRemoving}
            onClick={handleRemove}
            startIcon={isRemoving ? <CircularProgress size={12} /> : <CloseIcon sx={{ fontSize: '13px !important' }} />}
            sx={{
              height: 28, fontSize: '0.7rem', fontWeight: 700, textTransform: 'none',
              color: '#94A3B8', minWidth: 0, px: 0.75,
              borderRadius: '6px',
              '&:hover': { bgcolor: '#FEF2F2', color: '#DC2626' },
            }}
          >
            Clear
          </Button>
        </Box>
      )}

      {error && !isBillConflict && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.6, pl: 0.25 }}>
          <WarningAmberIcon sx={{ fontSize: 13, color: '#DC2626', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.68rem', color: '#DC2626', fontWeight: 600 }}>
            {error}
          </Typography>
        </Box>
      )}

      {isBillConflict && (
        <Box sx={{ mt: 0.75, p: 1.25, borderRadius: '8px', bgcolor: '#FEF9C3', border: '1px solid #FDE68A' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <WarningAmberIcon sx={{ fontSize: 13, color: '#CA8A04', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#92400E', fontWeight: 700 }}>
              Cannot combine with bill discount
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={handleRemoveBillDiscount}
            sx={{
              fontSize: '0.68rem', fontWeight: 700, textTransform: 'none',
              color: '#4F46E5', p: 0, minWidth: 0,
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
            }}
          >
            → Remove bill discount and apply coupon
          </Button>
        </Box>
      )}
    </Box>
  );
}
